<template>
  <v-container class="pa-4 pa-sm-8" fluid>
    <v-row>
      <v-col cols="12">
        <h2 class="dark--text text-h5 font-weight-bold">
          WhatsApp Settings
        </h2>
        <p class="text-body-2 grey--text mt-2">
          Manage Gallabox templates for different events. Each event can have only one template.
        </p>
      </v-col>
    </v-row>

    <!-- Global Settings Card -->
    <v-row class="mt-4">
      <v-col cols="12">
        <v-card outlined>
          <v-card-title class="text-subtitle-1">
            <v-icon left color="primary">mdi-cog</v-icon>
            Global Settings
          </v-card-title>
          <v-card-text>
            <v-row align="center">
              <v-col cols="12" sm="6" md="4">
                <v-switch v-model="enableAutoNotification" :loading="settingsLoading" :disabled="settingsLoading" color="success" inset @change="updateAutoNotificationSetting">
                  <template v-slot:label>
                    <span :class="enableAutoNotification ? 'success--text font-weight-medium' : 'grey--text'">
                      {{ enableAutoNotification ? "Auto Notifications Enabled" : "Auto Notifications Disabled" }}
                    </span>
                  </template>
                </v-switch>
              </v-col>
              <v-col cols="12" sm="6" md="8">
                <p class="text-body-2 grey--text mb-0">
                  When enabled, automatic WhatsApp notifications will be sent to customers based on configured templates.
                </p>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Available Events Card -->
    <v-row class="mt-4">
      <v-col cols="12" md="6">
        <v-card outlined>
          <v-card-title class="text-subtitle-1">
            <v-icon left color="primary">mdi-help-circle</v-icon>
            Available Events
          </v-card-title>
          <v-card-text>
            <v-chip v-for="event in allEventNames" :key="event" :color="usedEventNames.includes(event) ? 'success' : 'grey'" :outlined="!usedEventNames.includes(event)" small class="ma-1">
              <v-icon left small v-if="usedEventNames.includes(event)">mdi-check</v-icon>
              {{ formatEventName(event) }}
            </v-chip>
          </v-card-text>
        </v-card>
      </v-col>
    <v-col cols="12" md="6">
  <v-card outlined>
    <v-card-title class="text-subtitle-1">
      <v-icon left color="primary">mdi-help-circle</v-icon>
      Use only the variables listed below to create templates
    </v-card-title>

    <v-card-text>
      <!-- Available Variables Chips -->
      <v-chip
        v-for="event in availableTemplateVariables"
        :key="event"
        color="success"
        small
        class="ma-1"
      >
        {{ formatEventName(event) }}
      </v-chip>

      <!-- Informational Notes -->
      <div class="mt-4 text-body-2">
        <v-icon small color="primary">mdi-information</v-icon>
        <strong> InvoiceAmount ,  SubscriptionInvoiceUrl </strong> variables are
        only available in <strong>First Subscription Plan</strong> & 
        <strong>Second Subscription Onwards</strong> events.
      </div>

      <div class="mt-2 text-body-2">
        <v-icon small color="primary">mdi-information</v-icon>
        <strong> PurchaseInvoiceUrl ,  PurchaseAmount</strong> variables are
        only available in <strong>After Purchase From Shop</strong>.
      </div>

      <!-- Usage Sample -->
      <p class="text-body-2 grey--text mb-0 mt-4" v-pre>
        usage sample <br />
        Welcome {{ FirstName }} {{ LastName }} your subscription is Activated today.
      </p>
    </v-card-text>
  </v-card>
</v-col>

    </v-row>

    <v-row class="mt-4">
      <v-col cols="12">
        <v-card>
          <v-card-title class="d-flex justify-space-between align-center">
            <span>Gallabox Templates</span>
            <template-form :edit-template="selectedTemplate" :used-event-names="usedEventNames" @template-saved="onTemplateSaved" @close="selectedTemplate = null" />
          </v-card-title>

          <v-card-text>
            <template-list :templates="templates" :loading="loading" @edit-template="onEditTemplate" @template-deleted="onTemplateDeleted" />
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Manual Template Mappings Section -->
    <v-row class="mt-4">
      <v-col cols="12">
        <v-card>
          <v-card-title class="d-flex justify-space-between align-center">
            <span>
              <v-icon left color="primary">mdi-send</v-icon>
              Manual Template Send by Range
            </span>
            <v-btn color="primary" @click="openAddMappingDialog" :disabled="availableRanges.length === 0">
              <v-icon left>mdi-plus</v-icon>
              Add Template
            </v-btn>
          </v-card-title>

          <v-card-text>
            <!-- Mappings Table -->
            <v-data-table
              :mobile-breakpoint="0"
              :headers="mappingHeaders"
              :items="manualMappings"
              :loading="mappingsLoading"
              class="elevation-1"
              :footer-props="{ 'items-per-page-options': [10, 25, 50] }"
            >
              <template v-slot:item.range="{ item }">
                <v-chip small color="primary" text-color="white">
                  {{ formatRange(item.range) }}
                </v-chip>
              </template>

              <template v-slot:item.variables="{ item }">
                <span>{{ item.variables ? item.variables.join(', ') : '-' }}</span>
              </template>

              <template v-slot:item.active="{ item }">
                <v-icon v-if="item.active" color="success">mdi-check-circle</v-icon>
                <v-icon v-else color="grey">mdi-close-circle</v-icon>
              </template>

              <template v-slot:item.actions="{ item }">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      small
                      color="success"
                      v-bind="attrs"
                      v-on="on"
                      @click="sendByRange(item)"
                      :loading="item._sending"
                      :disabled="!item.active || sendingByRange"
                    >
                      <v-icon small>mdi-send</v-icon>
                    </v-btn>
                  </template>
                  <span>Send to all customers in this range</span>
                </v-tooltip>

                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      small
                      color="primary"
                      v-bind="attrs"
                      v-on="on"
                      @click="editMapping(item)"
                    >
                      <v-icon small>mdi-pencil</v-icon>
                    </v-btn>
                  </template>
                  <span>Edit</span>
                </v-tooltip>

                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      small
                      color="error"
                      v-bind="attrs"
                      v-on="on"
                      @click="confirmDeleteMapping(item)"
                    >
                      <v-icon small>mdi-delete</v-icon>
                    </v-btn>
                  </template>
                  <span>Delete</span>
                </v-tooltip>
              </template>
            </v-data-table>

            <!-- Send Result Alert -->
            <v-alert v-if="sendRangeResult" :type="sendRangeResult.success ? 'success' : 'error'" dismissible class="mt-4" @input="sendRangeResult = null">
              <strong>{{ sendRangeResult.message }}</strong>
              <div v-if="sendRangeResult.totalCustomers">
                Sent: {{ sendRangeResult.totalSent }} / {{ sendRangeResult.totalCustomers }} | Failed: {{ sendRangeResult.totalFailed }}
              </div>
            </v-alert>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Add/Edit Mapping Dialog -->
    <v-dialog v-model="mappingDialog" max-width="600" persistent>
      <v-card>
        <v-card-title>
          {{ editingMapping ? 'Edit Template Mapping' : 'Add Template Mapping' }}
        </v-card-title>
        <v-card-text>
          <v-form ref="mappingForm" v-model="mappingFormValid">
            <v-select
              v-model="mappingForm.range"
              :items="editingMapping ? allRanges : availableRanges"
              item-text="label"
              item-value="value"
              label="Subscription Range *"
              outlined
              dense
              :rules="[v => !!v || 'Range is required']"
              :disabled="!!editingMapping"
            />

            <v-autocomplete
              v-model="mappingForm.templateName"
              :items="gallaboxTemplates"
              item-text="name"
              item-value="name"
              label="Gallabox Template *"
              outlined
              dense
              :loading="gallaboxTemplatesLoading"
              :rules="[v => !!v || 'Template is required']"
              @change="onTemplateSelected"
              clearable
              placeholder="Search template name"
            >
              <template v-slot:item="{ item }">
                <v-list-item-content>
                  <v-list-item-title>{{ item.name }}</v-list-item-title>
                  <v-list-item-subtitle>
                    Variables: {{ item.variableCount }} | Status: {{ item.status }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </v-autocomplete>

            <v-textarea
              v-model="mappingForm.description"
              label="Description"
              outlined
              dense
              rows="2"
            />

            <v-switch
              v-model="mappingForm.active"
              label="Active"
              color="success"
              inset
            />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closeMappingDialog">Cancel</v-btn>
          <v-btn color="primary" :loading="savingMapping" :disabled="!mappingFormValid" @click="saveMapping">
            {{ editingMapping ? 'Update' : 'Create' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Mapping Confirmation Dialog -->
    <v-dialog v-model="deleteMappingDialog" max-width="400">
      <v-card>
        <v-card-title class="text-h6">Confirm Delete</v-card-title>
        <v-card-text>
          Are you sure you want to delete the template mapping for range "{{ selectedMappingForDelete ? formatRange(selectedMappingForDelete.range) : '' }}"?
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="deleteMappingDialog = false">Cancel</v-btn>
          <v-btn color="error" text @click="deleteMapping" :loading="deletingMapping">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Failed Send Logs Section -->
     <v-row class="mt-4">
      <v-col cols="12">
        <v-card>
          <v-card-title class="d-flex justify-space-between align-center">
            <span>
              <v-icon left color="error">mdi-alert-circle</v-icon>
              Failed Send Logs
            </span>
            <v-btn color="error" text @click="clearAllFailedLogs" :loading="clearingLogs" :disabled="failedLogs.length === 0">
              <v-icon left>mdi-delete-sweep</v-icon>
              Clear All
            </v-btn>
          </v-card-title>

          <v-card-text>
            <v-data-table
              :mobile-breakpoint="0"
              :headers="failedLogHeaders"
              :items="failedLogs"
              :loading="failedLogsLoading"
              class="elevation-1"
              :footer-props="{ 'items-per-page-options': [10, 25, 50] }"
            >
              <template v-slot:item.range="{ item }">
                <v-chip small color="primary" outlined>
                  {{ formatRange(item.range) }}
                </v-chip>
              </template>

              <template v-slot:item.createdAt="{ item }">
                {{ formatDate(item.createdAt) }}
              </template>

              <template v-slot:item.status="{ item }">
                <v-chip small :color="item.status === 'failed' ? 'error' : 'warning'" text-color="white">
                  {{ item.status }}
                </v-chip>
              </template>

              <template v-slot:item.errorMessage="{ item }">
                <v-btn
                  x-small
                  color="error"
                  outlined
                  @click="showErrorDetails(item)"
                >
                  <v-icon left x-small>mdi-eye</v-icon>
                  View Error
                </v-btn>
              </template>

              <template v-slot:item.actions="{ item }">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      small
                      color="error"
                      v-bind="attrs"
                      v-on="on"
                      @click="deleteFailedLog(item)"
                    >
                      <v-icon small>mdi-delete</v-icon>
                    </v-btn>
                  </template>
                  <span>Delete</span>
                </v-tooltip>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row> 

    <!-- Error Details Dialog -->
    <v-dialog v-model="errorDetailsDialog" max-width="600">
      <v-card>
        <v-card-title class="error white--text">
          <v-icon left color="white">mdi-alert-circle</v-icon>
          Error Details
        </v-card-title>
        <v-card-text class="pt-4" v-if="selectedErrorLog">
          <v-row>
            <v-col cols="12" sm="6">
              <div class="text-caption grey--text">Customer Name</div>
              <div class="text-body-1 font-weight-medium">{{ selectedErrorLog.customerName || '-' }}</div>
            </v-col>
            <v-col cols="12" sm="6">
              <div class="text-caption grey--text">Phone</div>
              <div class="text-body-1 font-weight-medium">{{ selectedErrorLog.customerPhone || '-' }}</div>
            </v-col>
            <v-col cols="12" sm="6">
              <div class="text-caption grey--text">Template</div>
              <div class="text-body-1 font-weight-medium">{{ selectedErrorLog.templateName || '-' }}</div>
            </v-col>
            <v-col cols="12" sm="6">
              <div class="text-caption grey--text">Range</div>
              <div class="text-body-1 font-weight-medium">{{ formatRange(selectedErrorLog.range) }}</div>
            </v-col>
            <v-col cols="12" sm="6">
              <div class="text-caption grey--text">Date</div>
              <div class="text-body-1 font-weight-medium">{{ formatDate(selectedErrorLog.createdAt) }}</div>
            </v-col>
            <v-col cols="12" sm="6">
              <div class="text-caption grey--text">Status</div>
              <v-chip small :color="selectedErrorLog.status === 'failed' ? 'error' : 'warning'" text-color="white">
                {{ selectedErrorLog.status }}
              </v-chip>
            </v-col>
            <v-col cols="12">
              <div class="text-caption grey--text">Error Message</div>
              <v-alert type="error" dense text class="mt-1 mb-0">
                {{ selectedErrorLog.errorMessage || 'Unknown error' }}
              </v-alert>
            </v-col>
            <v-col cols="12" v-if="selectedErrorLog.gallaboxResponse">
              <div class="text-caption grey--text">Gallabox Response</div>
              <pre class="error-response pa-2 mt-1">{{ selectedErrorLog.gallaboxResponse }}</pre>
            </v-col>
            <v-col cols="12" v-if="selectedErrorLog.requestPayload">
              <div class="text-caption grey--text">Request Payload</div>
              <pre class="error-response pa-2 mt-1">{{ selectedErrorLog.requestPayload }}</pre>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="errorDetailsDialog = false">Close</v-btn>
          <v-btn color="error" text @click="deleteFailedLogAndClose">
            <v-icon left>mdi-delete</v-icon>
            Delete Log
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="3000" top right>
      {{ snackbarText }}
    </v-snackbar>
  </v-container>
</template>

<script>
import TemplateForm from "../components/TemplateForm.vue";
import TemplateList from "../components/TemplateList.vue";

export default {
  components: {
    TemplateForm,
    TemplateList,
  },
  data() {
    return {
      templates: [],
      loading: false,
      selectedTemplate: null,
      enableAutoNotification: false,
      settingsLoading: false,
      gallaboxTemplates: [],
      gallaboxTemplatesLoading: false,
      // Manual Template Mappings
      manualMappings: [],
      mappingsLoading: false,
      mappingDialog: false,
      editingMapping: null,
      mappingFormValid: false,
      savingMapping: false,
      mappingForm: {
        range: null,
        templateName: null,
        templateBody: '',
        variableCount: 0,
        variables: [],
        description: '',
        active: true,
      },
      mappingHeaders: [
        { text: "Range", value: "range", sortable: true },
        { text: "Template Name", value: "templateName", sortable: true },
        { text: "Variable Count", value: "variableCount", sortable: true },
        { text: "Variables", value: "variables", sortable: false },
        { text: "Status", value: "active", sortable: true },
        { text: "Actions", value: "actions", sortable: false, align: "center" },
      ],
      allRanges: [
        { label: "0-3 Subscriptions", value: "0-3" },
        { label: "4-6 Subscriptions", value: "4-6" },
        { label: "7-10 Subscriptions", value: "7-10" },
        { label: "10+ Subscriptions", value: "10+" },
      ],
      deleteMappingDialog: false,
      selectedMappingForDelete: null,
      deletingMapping: false,
      sendingByRange: false,
      sendRangeResult: null,
      // Failed Logs
      failedLogs: [],
      failedLogsLoading: false,
      clearingLogs: false,
      errorDetailsDialog: false,
      selectedErrorLog: null,
      failedLogHeaders: [
        { text: "Range", value: "range", sortable: true },
        { text: "Template", value: "templateName", sortable: true },
        { text: "Customer", value: "customerName", sortable: true },
        { text: "Phone", value: "customerPhone", sortable: false },
        { text: "Error", value: "errorMessage", sortable: false, width: "120px" },
        { text: "Date", value: "createdAt", sortable: true },
        { text: "Status", value: "status", sortable: true },
        { text: "Actions", value: "actions", sortable: false, align: "center" },
      ],
      // Snackbar
      snackbar: false,
      snackbarText: '',
      snackbarColor: 'success',
      allEventNames: ["next_day_after_start_date", "Fifth_day_after_start_date", "Three_days_before_expiry", "six_days_after_expiry", "twelve_days_after_expiry", "first_subscription_plan", "second_subscription_onwards", "after_purchase_from_shop", "adding_new_product_to_shop","twelve_day_after_start_date", "three_days_after_expiry"],
      availableTemplateVariables: ["firstName", "lastName", "planName", "purchaseDate", "planStartDate", "planEndDate", "invoiceAmount", "subscriptionInvoiceUrl","purchaseInvoiceUrl","purchaseAmount"],
    };
  },
  computed: {
    usedEventNames() {
      return this.templates.map(t => t.eventName);
    },
    availableRanges() {
      const usedRanges = this.manualMappings.map(m => m.range);
      return this.allRanges.filter(r => !usedRanges.includes(r.value));
    },
  },
  created() {
    this.fetchTemplates();
    this.fetchSettings();
    this.fetchGallaboxTemplates();
    this.fetchManualMappings();
    this.fetchFailedLogs();
  },
  methods: {
    fetchTemplates() {
      this.loading = true;
      this.$apollo
        .query({
          query: require("../api/getAllGallaboxTemplates.graphql"),
          variables: {
            input: {
              skip: 0,
              limit: 100,
            },
          },
          fetchPolicy: "network-only",
        })
        .then(({ data }) => {
          this.templates = data.getAllGallaboxTemplates.templates;
          this.loading = false;
        })
        .catch(error => {
          console.error("Error fetching templates:", error);
          this.loading = false;
        });
    },
    onTemplateSaved() {
      this.selectedTemplate = null;
      this.fetchTemplates();
    },
    onTemplateDeleted() {
      this.fetchTemplates();
    },
    onEditTemplate(template) {
      this.selectedTemplate = { ...template };
    },
    formatEventName(eventName) {
      return eventName.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
    },
    fetchSettings() {
      this.settingsLoading = true;
      this.$apollo
        .query({
          query: require("../api/getGallaboxSettings.graphql"),
          fetchPolicy: "network-only",
        })
        .then(({ data }) => {
          this.enableAutoNotification = data.getGallaboxSettings.enableAutoNotification;
          this.settingsLoading = false;
        })
        .catch(error => {
          console.error("Error fetching settings:", error);
          this.settingsLoading = false;
        });
    },
    updateAutoNotificationSetting(value) {
      this.settingsLoading = true;
      this.$apollo
        .mutate({
          mutation: require("../api/updateGallaboxSettings.graphql"),
          variables: {
            input: {
              enableAutoNotification: value,
            },
          },
        })
        .then(({ data }) => {
          this.enableAutoNotification = data.updateGallaboxSettings.enableAutoNotification;
          this.settingsLoading = false;
        })
        .catch(error => {
          console.error("Error updating settings:", error);
          // Revert on error
          this.enableAutoNotification = !value;
          this.settingsLoading = false;
        });
    },
    fetchGallaboxTemplates() {
      this.gallaboxTemplatesLoading = true;
      this.$apollo
        .query({
          query: require("../api/getAllTemplatesFromGallabox.graphql"),
          fetchPolicy: "network-only",
        })
        .then(({ data }) => {
          this.gallaboxTemplates = data.getAllTemplatesFromGallabox.templates || [];
          this.gallaboxTemplatesLoading = false;
        })
        .catch(error => {
          console.error("Error fetching Gallabox templates:", error);
          this.gallaboxTemplates = [];
          this.gallaboxTemplatesLoading = false;
        });
    },
    // Manual Template Mappings Methods
    fetchManualMappings() {
      this.mappingsLoading = true;
      this.$apollo
        .query({
          query: require("../api/getAllManualTemplateMappings.graphql"),
          fetchPolicy: "network-only",
        })
        .then(({ data }) => {
          this.manualMappings = data.getAllManualTemplateMappings.mappings || [];
          this.mappingsLoading = false;
        })
        .catch(error => {
          console.error("Error fetching manual mappings:", error);
          this.manualMappings = [];
          this.mappingsLoading = false;
        });
    },
    openAddMappingDialog() {
      this.editingMapping = null;
      this.mappingForm = {
        range: null,
        templateName: null,
        templateBody: '',
        variableCount: 0,
        variables: [],
        description: '',
        active: true,
      };
      this.mappingDialog = true;
    },
    editMapping(mapping) {
      this.editingMapping = mapping;
      this.mappingForm = {
        range: mapping.range,
        templateName: mapping.templateName,
        templateBody: mapping.templateBody || '',
        variableCount: mapping.variableCount || 0,
        variables: mapping.variables || [],
        description: mapping.description || '',
        active: mapping.active,
      };
      this.mappingDialog = true;
    },
    closeMappingDialog() {
      this.mappingDialog = false;
      this.editingMapping = null;
      if (this.$refs.mappingForm) {
        this.$refs.mappingForm.reset();
      }
    },
    onTemplateSelected(templateName) {
      const template = this.gallaboxTemplates.find(t => t.name === templateName);
      if (template) {
        this.mappingForm.templateBody = template.body || '';
        // Extract variables from template body
        const variableMatches = (template.body || '').match(/\{\{[^}]+\}\}/g) || [];
        this.mappingForm.variableCount = variableMatches.length;
        this.mappingForm.variables = variableMatches.map(v => v.replace(/\{\{|\}\}/g, '').trim());
      }
    },
    saveMapping() {
      if (!this.$refs.mappingForm.validate()) return;

      this.savingMapping = true;
      const input = {
        range: this.mappingForm.range,
        templateName: this.mappingForm.templateName,
        templateBody: this.mappingForm.templateBody,
        variableCount: this.mappingForm.variableCount,
        variables: this.mappingForm.variables,
        description: this.mappingForm.description,
        active: this.mappingForm.active,
      };

      if (this.editingMapping) {
        // Update existing
        this.$apollo
          .mutate({
            mutation: require("../api/updateManualTemplateMapping.graphql"),
            variables: { id: this.editingMapping._id, input },
          })
          .then(() => {
            this.showSnackbar("Template mapping updated successfully", "success");
            this.closeMappingDialog();
            this.fetchManualMappings();
          })
          .catch(error => {
            console.error("Error updating mapping:", error);
            this.showSnackbar(error.message || "Failed to update mapping", "error");
          })
          .finally(() => {
            this.savingMapping = false;
          });
      } else {
        // Create new
        this.$apollo
          .mutate({
            mutation: require("../api/createManualTemplateMapping.graphql"),
            variables: { input },
          })
          .then(() => {
            this.showSnackbar("Template mapping created successfully", "success");
            this.closeMappingDialog();
            this.fetchManualMappings();
          })
          .catch(error => {
            console.error("Error creating mapping:", error);
            this.showSnackbar(error.message || "Failed to create mapping", "error");
          })
          .finally(() => {
            this.savingMapping = false;
          });
      }
    },
    confirmDeleteMapping(mapping) {
      this.selectedMappingForDelete = mapping;
      this.deleteMappingDialog = true;
    },
    deleteMapping() {
      if (!this.selectedMappingForDelete) return;

      this.deletingMapping = true;
      this.$apollo
        .mutate({
          mutation: require("../api/deleteManualTemplateMapping.graphql"),
          variables: { id: this.selectedMappingForDelete._id },
        })
        .then(() => {
          this.showSnackbar("Template mapping deleted successfully", "success");
          this.deleteMappingDialog = false;
          this.selectedMappingForDelete = null;
          this.fetchManualMappings();
        })
        .catch(error => {
          console.error("Error deleting mapping:", error);
          this.showSnackbar(error.message || "Failed to delete mapping", "error");
        })
        .finally(() => {
          this.deletingMapping = false;
        });
    },
    sendByRange(mapping) {
      this.sendingByRange = true;
      this.sendRangeResult = null;
      this.$set(mapping, '_sending', true);

      this.$apollo
        .mutate({
          mutation: require("../api/sendTemplateByRange.graphql"),
          variables: { input: { range: mapping.range } },
        })
        .then(({ data }) => {
          this.sendRangeResult = data.sendTemplateByRange;
          this.fetchFailedLogs(); // Refresh failed logs
        })
        .catch(error => {
          console.error("Error sending by range:", error);
          this.sendRangeResult = {
            success: false,
            message: error.message || "Failed to send messages",
          };
        })
        .finally(() => {
          this.sendingByRange = false;
          this.$set(mapping, '_sending', false);
        });
    },
    formatRange(range) {
      if (range === '10+') return '10+ Subscriptions';
      return `${range} Subscriptions`;
    },
    // Failed Logs Methods
    fetchFailedLogs() {
      this.failedLogsLoading = true;
      this.$apollo
        .query({
          query: require("../api/getFailedSendLogs.graphql"),
          variables: { input: { limit: 100 } },
          fetchPolicy: "network-only",
        })
        .then(({ data }) => {
          this.failedLogs = data.getFailedSendLogs.logs || [];
          this.failedLogsLoading = false;
        })
        .catch(error => {
          console.error("Error fetching failed logs:", error);
          this.failedLogs = [];
          this.failedLogsLoading = false;
        });
    },
    deleteFailedLog(log) {
      this.$apollo
        .mutate({
          mutation: require("../api/deleteFailedSendLog.graphql"),
          variables: { id: log._id },
        })
        .then(() => {
          this.showSnackbar("Log deleted successfully", "success");
          this.fetchFailedLogs();
        })
        .catch(error => {
          console.error("Error deleting log:", error);
          this.showSnackbar(error.message || "Failed to delete log", "error");
        });
    },
    clearAllFailedLogs() {
      this.clearingLogs = true;
      this.$apollo
        .mutate({
          mutation: require("../api/clearFailedSendLogs.graphql"),
        })
        .then(() => {
          this.showSnackbar("All logs cleared successfully", "success");
          this.fetchFailedLogs();
        })
        .catch(error => {
          console.error("Error clearing logs:", error);
          this.showSnackbar(error.message || "Failed to clear logs", "error");
        })
        .finally(() => {
          this.clearingLogs = false;
        });
    },
   formatDate(dateStr) {
  if (!dateStr) return '-';

  const date = new Date(dateStr);

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();

  const time = date.toLocaleTimeString('en-US'); // keeps AM/PM format

  return `${day}/${month}/${year} ${time}`;
},
    showSnackbar(text, color = 'success') {
      this.snackbarText = text;
      this.snackbarColor = color;
      this.snackbar = true;
    },
    showErrorDetails(log) {
      this.selectedErrorLog = log;
      this.errorDetailsDialog = true;
    },
    deleteFailedLogAndClose() {
      if (!this.selectedErrorLog) return;
      this.deleteFailedLog(this.selectedErrorLog);
      this.errorDetailsDialog = false;
      this.selectedErrorLog = null;
    },
  },
};
</script>

<style scoped>
pre {
  white-space: pre-wrap;
  word-wrap: break-word;
}
.error-response {
  background-color: #f5f5f5;
  border-radius: 4px;
  font-size: 12px;
  overflow-x: auto;
  max-height: 150px;
  overflow-y: auto;
}
</style>
